import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Observable, throwError } from 'rxjs';
import { CareTeamMember } from 'src/app/models/care-team.model';
import { ReportJobResponse } from 'src/app/models/websocket.model';
import { NewAuthService } from 'src/app/services/auth/new-auth-service.service';
import { InitialDataModel } from 'src/app/services/models/initial-data-model.service';
import { environment } from 'src/environments/environment';
import { BuildFilterQueryOptions, PaginationParam, SearchPatientParams } from './patient-list.model';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PatientListService {
  collectionFSName = 'users';
  clientResponsibleIdFSName = 'client_responsible_id';
  lastNameFSName = 'lastName';
  rolesIsPatientFSQuery = 'roles.isPatient';
  maxClientsByProvider = 8;
  elasticSearchPatientEndpoint = `${environment.welbyEndpoint}/api/v1/core/patients/search`;
  patientsEndpoint = `${environment.welbyEndpoint}/api/v1/core/patients`;

  constructor(
    private firestore: AngularFirestore,
    private auth: NewAuthService,
    private initialModelService: InitialDataModel,
    private http: HttpClient
  ) {}

  getPatientById(id: string): Observable<any> {
    return this.firestore.collection('users').doc(id).valueChanges();
  }

  /**
   * Get patients from elastic search
   */
  filterPatientsFromElasticSearch(
    pageSize: number,
    searchParams?: SearchPatientParams,
    paginationParams?: PaginationParam,
    flatResults = false
  ) {
    let clientAccounts = searchParams?.client_responsible_id
      ? searchParams.client_responsible_id
      : this.initialModelService.clientAccounts;
    if (this.auth.user.roles.isAdmin) {
      clientAccounts = [];
    }
    const provider = searchParams?.provider?.isHold
      ? { firstName: this.auth.user.firstName, lastName: this.auth.user.lastName, user_id: this.auth.user.user_id }
      : { firstName: '', lastName: '', user_id: '' };
    let active: boolean = null;
    let deceased: boolean = null;
    delete searchParams?.client_responsible_id;
    if (searchParams?.searchTerms && searchParams?.searchTerms.length === 0) {
      delete searchParams.searchTerms;
    }
    if (searchParams?.active_rpm === true) {
      active = true;
      delete searchParams?.active_rpm;
    } else if (searchParams?.active_rpm === false) {
      active = false;
      delete searchParams?.active_rpm;
    }
    if (searchParams?.deceased === true) {
      deceased = true;
      delete searchParams?.deceased;
    } else if (searchParams?.deceased === false) {
      deceased = false;
      delete searchParams?.deceased;
    }
    const elasticQuery = this.buildFilterQuery(searchParams, clientAccounts, { active, deceased }, provider);
    return this.http.post(this.elasticSearchPatientEndpoint, {
      elasticQuery,
      pageSize,
      page: paginationParams.page,
      sort: paginationParams.sort,
      flatResults,
    });
  }

  /**
   * Build filter query for elastic search
   */
  buildFilterQuery(
    searchParams: SearchPatientParams,
    clientAccounts: string[],
    state: { active: boolean | null; deceased: boolean | null } = { active: null, deceased: null },
    provider: { firstName: string; lastName: string; user_id: string } = { firstName: '', lastName: '', user_id: '' },
    options: BuildFilterQueryOptions = {}
  ) {
    const query: any = {
      query: {
        bool: {
          must: [
            {
              bool: {
                should: [
                  {
                    match: {
                      'roles.isPatient': true,
                    },
                  },
                ],
              },
            },
          ],
        },
      },
    };
    if (clientAccounts.length > 0) {
      query.query.bool.must.push({
        bool: {
          should: clientAccounts.map((client) => ({
            match: {
              client_responsible_id: client,
            },
          })),
        },
      });
    }
    if (provider.user_id !== '') {
      delete searchParams.provider;
      // `_name` is nested INSIDE the field, which is the only form Elasticsearch accepts. As a
      // sibling of the field it is a second field, and the parser refuses:
      //
      //   parsing_exception: [match] query doesn't support multiple fields,
      //                      found [provider_id] and [_name]
      //
      // The sibling form is what this clause carried until now, and it had never run: `setParams()`
      // — the only place that sets `provider.isHold`, which is the only thing that makes this clause
      // be emitted at all — feeds `filterPatientsFromFirestore`, not this builder. The Elastic path
      // reaches it only when a search TERM is typed with "Show my patients" on, and that combination
      // has been answering 500 in production. Bulk Transfer is the first caller to hit it every time,
      // which is how it surfaced.
      //
      // So the default branch is NOT byte-identical to what it emitted before, and cannot be: the
      // previous output was rejected by Elasticsearch. The change is confined to moving `_name`
      // inside the field, so no clause is added, removed or re-scoped and no result set can shift —
      // there were no results to preserve.
      const namedMatch = (field: string) => ({
        match: {
          [field]: {
            query: provider.user_id,
            _name: field,
          },
        },
      });
      // Only Bulk Transfer asks for 'any', because a Medical Assistant is usually NOT a patient's
      // `provider_id` — she is one entry in `providers_ids` — so matching the primary alone returns
      // an empty panel for her. `minimum_should_match` is spelled out on that branch only: with two
      // clauses the implicit default of 1 is what we want, but leaving it implicit means a later
      // `must`/`filter` added to the same bool would silently turn both into optional scoring
      // clauses and widen the panel.
      if (options.providerMatch === 'any') {
        query.query.bool.must.push({
          bool: {
            should: [namedMatch('provider_id'), namedMatch('providers_ids')],
            minimum_should_match: 1,
          },
        });
      } else {
        query.query.bool.must.push({
          bool: {
            should: [namedMatch('provider_id')],
          },
        });
      }
    }
    if (state.active !== null) {
      query.query.bool.must.push({
        bool: {
          should: [
            {
              match: { 'status.active': state.active },
            },
          ],
        },
      });
    }
    if (state.deceased !== null) {
      query.query.bool.must.push({
        bool: {
          should: [
            {
              match: { 'status.deceased': state.deceased },
            },
          ],
        },
      });
    }
    if (Object.keys(searchParams).length > 0) {
      query.query.bool.must.push({
        bool: {
          should: Object.keys(searchParams).map((param) => {
            if (typeof searchParams[param] === 'object') {
              return {
                bool: {
                  should: Object.keys(searchParams[param]).map((itemKey) => ({
                    match_phrase_prefix: {
                      [`${param}.${itemKey}`]: {
                        query: searchParams[param][itemKey],
                        _name: `${param}.${itemKey}`,
                      },
                    },
                  })),
                },
              };
            }
            return {
              match_phrase_prefix: {
                [param]: {
                  query: searchParams[param],
                  _name: param,
                },
              },
            };
          }),
        },
      });
    }
    if (state.active !== null) {
      searchParams.active_rpm = state.active;
    }
    if (state.deceased !== null) {
      searchParams.deceased = state.deceased;
    }
    if (clientAccounts.length === 1) {
      searchParams.client_responsible_id = clientAccounts;
    }
    return query;
  }

  /**
   * Get patients from firestore
   */
  filterPatientsFromFirestore(
    pageSize: number,
    searchParams?: SearchPatientParams,
    paginationParams?: PaginationParam
  ) {
    if (!this.auth.user.roles.isAdmin) {
      searchParams.client_responsible_id = searchParams.client_responsible_id
        ? searchParams.client_responsible_id
        : this.initialModelService.clientAccounts;
    }
    return this.http
      .post(this.patientsEndpoint, {
        pageSize,
        page: paginationParams.page,
        sort: paginationParams.sort,
        searchParams,
      })
      .pipe(
        catchError((error) => {
          console.error('Error fetching patients', error);
          return throwError(() => error);
        })
      );
  }

  getCareProviders(clientsId: string[]): Observable<CareTeamMember[]> {
    return this.firestore
      .collection('care_providers', (ref) => ref.where('client_responsible_id', 'in', clientsId))
      .valueChanges();
  }

  getChipColor(program: string): string {
    const colors = {
      rpm: 'primary',
      ccm: 'accent',
    };
    return colors[program] || 'default';
  }

  exportPatients(
    status: string,
    clientId?: string,
    clientName?: string,
    provider?: { firstName: string; lastName: string; uid: string; user_id: string }
  ): Observable<ReportJobResponse> {
    return this.http.post<ReportJobResponse>(`${environment.welbyEndpoint}/api/v1/core/patients/patientsReport`, {
      clientId,
      status,
      clientName,
      provider,
    });
  }

  async showExcel(response, clientName: string): Promise<void> {
    const file = this.createExcelFile(response);
    const url = window.URL.createObjectURL(file);
    const date = new Date();
    const a = document.createElement('a');
    a.href = url;
    a.download = `${clientName}-${date.toLocaleDateString()}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  createExcelFile(response): Blob {
    return new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  }

  getClientDepartments(clientId: string): Observable<any> {
    return this.firestore
      .collection(`client_departments`, (ref) => ref.where('client_id', '==', clientId))
      .valueChanges({ idField: 'id' });
  }
}
